/**
 * THE WORKING RELATIONSHIP with one provider — tenant-private.
 *
 * Three kinds of thing sit here and are never blended:
 *
 *   INTERACTION FACTS  what actually happened in your own records: quotes,
 *                      invitations, bookings, memberships.
 *   NAMED DERIVATIONS  counts with their denominators stated. A response rate
 *                      is "4 of 8 answerable invitations", not a grade.
 *   HUMAN STATEMENTS   what people at your company have said about working
 *                      with them. Append-only, attributed, and never leaving
 *                      this tenant.
 *
 * What is deliberately absent: any score, grade, star rating, tier or health
 * colour. The backend refuses to compute one and says so in its own payload;
 * dividing "would use again" by "statements" here would manufacture exactly
 * the number it declined to produce.
 *
 * The other rule that matters: absence is UNKNOWN, never poor performance. A
 * provider you have never invited has no response rate — reporting that as 0%
 * would invent a failure out of a gap.
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { UnknownValue } from "../../components/workflow/WorkflowStates";
import {
  presentResponseRate,
  presentStance,
  presentRelationshipEvent,
  hasConfirmedIdentity,
  RELATIONSHIP_EVENT_TYPES,
} from "./exchangePresentation";
import {
  Handshake,
  MessageSquarePlus,
  Loader2,
  Lock,
  AlertTriangle,
  History,
} from "lucide-react";

/** One derived count, always shown with what it was counted out of. */
const DerivedCount = ({ label, value, detail, capped }) => (
  <div className="rounded-lg bg-muted/30 p-3 space-y-0.5">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-xl font-bold">{value}</p>
    {detail && <p className="text-[11px] text-muted-foreground">{detail}</p>}
    {capped && (
      <p className="text-[10px] text-muted-foreground">
        Only the first 50 are listed; the count above is the full figure.
      </p>
    )}
  </div>
);

const RelationshipPanel = ({
  context,
  canRecord,
  recording,
  onRecord,
  organizationName,
}) => {
  const [open, setOpen] = useState(false);
  const [eventType, setEventType] = useState("");
  const [occurredOn, setOccurredOn] = useState("");
  const [note, setNote] = useState("");

  const close = () => {
    setOpen(false);
    setEventType("");
    setOccurredOn("");
    setNote("");
  };

  const submit = async () => {
    if (!eventType) return;
    const ok = await onRecord({
      eventType,
      // An unstated date is unknown — the backend never defaults it to today,
      // and neither does this form.
      ...(occurredOn ? { occurredOn } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    if (ok) close();
  };

  if (!context) return null;

  const confirmed = hasConfirmedIdentity(context);
  const facts = context.interactionFacts || {};
  const derived = context.derived || {};
  const responseRate = presentResponseRate(derived.responseRate);
  const stance = presentStance(derived.wouldUseAgain);
  const stated = context.statedEvents || {};
  const events = (stated.recent || []).map(presentRelationshipEvent);

  const awards = derived.awards || {};
  const together = derived.projectsTogether || {};

  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Handshake className="w-4 h-4 text-primary" />
            Your working relationship
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-muted/50 border-border font-normal text-[10px] gap-1"
              title={context.privacy}
            >
              <Lock className="w-3 h-3" />
              Private to your company
            </Badge>
            {canRecord && (
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(true)}>
                <MessageSquarePlus className="w-3.5 h-3.5 mr-1.5" />
                Record a statement
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!confirmed && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <p className="text-xs text-amber-400 font-medium">
              No identity link to this organization is confirmed yet.
            </p>
            <p className="text-[11px] text-muted-foreground">
              Everything below reads only from records joined by a confirmed link, so it is
              empty because the identity is unconfirmed — not because you have no history.
              Confirm a match under Identity linkage above.
            </p>
          </div>
        )}

        {/* What actually happened, from your own records */}
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
            In your records
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {[
              ["Quotes", facts.quotes?.count],
              ["Selected", facts.quotes?.selectedCount],
              ["Bookings", facts.bookings?.count],
              ["Expressions of interest", facts.interests?.count],
            ].map(([label, value]) => (
              <div key={label} className="rounded bg-muted/30 px-2 py-1.5">
                <p className="text-[10px] text-muted-foreground">{label}</p>
                <p className="text-sm font-semibold">
                  {typeof value === "number" ? value : <UnknownValue />}
                </p>
              </div>
            ))}
          </div>
          {/* This flag names a cut in the itemised source LIST. The counts
              above it are exact totals over every row read — reporting them
              as floors would understate a provider's real history. Genuine
              floor-ness is a different condition and the backend names it
              separately, in missingInformation. */}
          {facts.sourcesTruncated && (
            <p className="text-[10px] text-muted-foreground mt-1">
              The counts are complete; the underlying record list was too long to itemise.
            </p>
          )}
        </div>

        {/* Derived counts, each with its denominator */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg bg-muted/30 p-3 space-y-0.5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Response rate
            </p>
            {responseRate.known ? (
              <>
                <p className="text-xl font-bold">{responseRate.percent}%</p>
                <p className="text-[11px] text-muted-foreground">{responseRate.detail}</p>
              </>
            ) : (
              <>
                <UnknownValue reason={responseRate.detail} />
                <p className="text-[11px] text-muted-foreground">{responseRate.detail}</p>
              </>
            )}
          </div>

          <DerivedCount
            label="Packages awarded"
            value={awards.count ?? 0}
            detail="A count of awards, not a rating."
            capped={(awards.count || 0) > (awards.bidPackageIds || []).length}
          />
          <DerivedCount
            label="Projects together"
            value={together.count ?? 0}
            capped={(together.count || 0) > (together.projectIds || []).length}
          />
        </div>

        {/* Human stance — tallies, never a ratio */}
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
            What your team says
          </p>
          {stance.known ? (
            <div className="flex flex-wrap gap-2">
              <Badge
                variant="outline"
                className="bg-emerald-500/15 text-emerald-400 border-emerald-500/25 font-normal"
              >
                {stance.wouldUseAgain} would use again
              </Badge>
              <Badge
                variant="outline"
                className="bg-red-500/15 text-red-400 border-red-500/25 font-normal"
              >
                {stance.doNotRecommend} would not recommend
              </Badge>
              {stance.preferred > 0 && (
                <Badge
                  variant="outline"
                  className="bg-blue-500/15 text-blue-400 border-blue-500/25 font-normal"
                >
                  {stance.preferred} marked preferred
                </Badge>
              )}
              <span className="text-[11px] text-muted-foreground self-center">
                across {stance.statements} statement{stance.statements === 1 ? "" : "s"}
              </span>
            </div>
          ) : (
            <UnknownValue reason={stance.detail} />
          )}
        </div>

        {/* The statements themselves */}
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1">
            <History className="w-3 h-3" />
            Recorded statements
            {typeof stated.count === "number" && (
              <span className="normal-case tracking-normal">({stated.count})</span>
            )}
          </p>
          {events.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Nobody has recorded a statement about this provider.
            </p>
          ) : (
            <div className="space-y-1.5">
              {events.map((event) => (
                <div key={event.id} className="rounded bg-muted/30 px-2.5 py-2 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium">{event.label}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {event.dateKnown ? event.occurredOn : "date not stated"}
                    </span>
                  </div>
                  {event.note && <p className="text-xs text-muted-foreground">{event.note}</p>}
                </div>
              ))}
            </div>
          )}
          {stated.recentTruncated && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Only the most recent statements are listed.
            </p>
          )}
        </div>

        {(context.missingInformation || []).length > 0 && (
          <div className="space-y-0.5 border-t border-border/50 pt-2">
            {context.missingInformation.map((line, i) => (
              <p key={i} className="text-[11px] text-muted-foreground flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                {line}
              </p>
            ))}
          </div>
        )}

        {/* The backend states this rule in its own payload. Rendering its
            sentence rather than a paraphrase means the guard stays true if
            the backend ever rewords it. */}
        <p className="text-[10px] text-muted-foreground">
          {derived.note ||
            "These are counts and statements, not a score. OMNΛS grades no provider, and none of this affects whether a provider is eligible for a package."}
        </p>
      </CardContent>

      <Dialog open={open} onOpenChange={(next) => !next && !recording && close()}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record a statement{organizationName ? ` — ${organizationName}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This records something a person at your company is asserting. Facts the system
              already holds — that they were invited, bid, or were awarded work — are not
              recorded here; they live in the records that own them.
            </p>
            <div>
              <Label className="text-xs">What are you recording?</Label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a statement" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_EVENT_TYPES.map((row) => (
                    <SelectItem key={row.value} value={row.value}>
                      {row.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs" htmlFor="relationship-occurred-on">
                When did it happen? (optional)
              </Label>
              <Input
                id="relationship-occurred-on"
                type="date"
                className="mt-1"
                value={occurredOn}
                onChange={(event) => setOccurredOn(event.target.value)}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Leave blank if you do not know — it is recorded as undated rather than as
                today.
              </p>
            </div>
            <div>
              <Label className="text-xs" htmlFor="relationship-note">
                Context (optional)
              </Label>
              <Textarea
                id="relationship-note"
                className="mt-1"
                rows={3}
                maxLength={4000}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="What happened, in your words"
              />
            </div>
            <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2.5 py-2">
              <p className="text-[11px] text-amber-400 font-medium">This cannot be edited or deleted.</p>
              <p className="text-[11px] text-muted-foreground">
                Statements are a permanent record. If your view changes later, you record a new
                statement — the earlier one stays visible.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={close} disabled={recording}>
                Cancel
              </Button>
              <Button size="sm" onClick={submit} disabled={recording || !eventType}>
                {recording && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Record statement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RelationshipPanel;
